import Button from '@brands/Components/Button/Button';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { resendConfirmationCode } from '@brands/services/auth/resendConfirmationCode';
import { verifyConfirmationCode } from '@brands/services/auth/verifyConfirmationCode';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatEmail } from '@brands/Utils/formatDate';
import { yupResolver } from '@hookform/resolvers/yup';
import { MuiOtpInput } from 'mui-one-time-password-input';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { config } from '../../config/config';
import styles from './styles.module.scss';

interface Props {
  username: string;
  onConfirmed: () => void;
}

type UserSubmitForm = {
  code: string;
};

const ConfirmOTP: FC<Props> = ({ username, onConfirmed }) => {
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required('Verification Code is required')
      .matches(/^\d{6}$/, 'The code must be a 6-digit number.'),
  });

  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UserSubmitForm>({
    resolver: yupResolver(validationSchema),
  });

  const handleChange = (newValue: string): void => {
    setOtp(newValue);
    clearErrors('code');
  };

  const matchIsNumeric = (text: string): boolean => {
    return text.trim() !== '' && !isNaN(Number(text));
  };

  const validateChar = (value: string): boolean => {
    return matchIsNumeric(value);
  };

  const validateCode = async (newValue: string): Promise<void> => {
    setLoading(true);
    try {
      await verifyConfirmationCode({
        email: username?.toLowerCase(),
        confirmation_code: newValue,
      });
      onConfirmed();
    } catch (error) {
      displayErrorDetails(error);
      setError('code', {
        type: 'manual',
        message: 'The code you entered is invalid. Please enter the most recent verification code sent to your email.',
      });
      setLoading(false);
    }
  };

  const onResend = async (): Promise<void> => {
    try {
      await resendConfirmationCode({
        email: username?.toLowerCase(),
      });
    } finally {
      toast.success('Successfully sent you the verification code.');
    }
  };

  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog />
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <form className={styles.formContainer}>
            <span className={`${styles.formTitle} p-b-26 fs-unmask`}>
              {config.organization_id === 'dentistry-one' ? (
                <span className={styles.formTitleWelcome}>
                  Welcome to dentistry.<span className={styles.formTitleOne}>one</span>
                </span>
              ) : (
                <span className={styles.formTitleWelcome}>
                  Welcome to{' '}
                  <span className={styles.formTitleOne} style={{ display: 'block' }}>
                    {config.organization_id === 'dentistry-one'
                      ? 'dentistry.one'
                      : config.organization_id
                          .split('-')
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(' ')}
                  </span>
                </span>
              )}
            </span>
            <span className={`${styles.welcomeText} fs-unmask`} data-testid="confirm-otp-title">
              {`We sent a verification code to ${formatEmail(username)}`}
            </span>
            <div className={styles.inputWrapper}>
              <MuiOtpInput
                className="fs-exclude"
                autoFocus
                value={otp}
                onChange={handleChange}
                length={6}
                onComplete={validateCode}
                TextFieldsProps={{
                  placeholder: '#',
                  disabled: loading,
                  inputMode: 'numeric',
                  type: 'tel',
                }}
                validateChar={validateChar}
              />

              <div className={`${styles.invalidFeedback} fs-unmask`}>{errors.code && errors.code.message}</div>
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonWrapper}>
                <Button
                  type="button"
                  onClick={onResend}
                  className={styles.formBtn}
                  disabled={loading}
                  data-testid="confirm-otp-resend-code"
                >
                  Resend code
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ConfirmOTP;
